import React, { useEffect, useState } from "react";
import {
  Paper,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  tableCellClasses,
  styled,
  Box,
  Typography,
  Link,
  Badge,
  Grid,
  ButtonGroup,
  Button,
} from "@mui/material";
import useTable from "../common/UseTable";
import Popup from "../common/Popup";
import Controls from "../controls/Controls";
import MessageIcon from "@mui/icons-material/Message";
import BugReportIcon from "@mui/icons-material/BugReport";
import VerifiedIcon from "@mui/icons-material/Verified";
import TaskIcon from "@mui/icons-material/Task";
import ReviewForm from "./ReviewForm";
import DiscussionPanel from "./DiscussionPanel";
import SearchPanel from "./SearchPanel";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Collapse from "@mui/material/Collapse";
import { useDispatch, useSelector } from "react-redux";
import {
  loadIssues,
  getAllIssues,
  notifyPortalUser,
  closedIssueTicket,
  loadIssuesByfilter,
} from "../../store/issues";
import Notification from "../common/Notification";
import { useConfirm } from "material-ui-confirm";
import { format, set } from "date-fns";
import { documentDownloaded, download, downloadDocument, getDownloadedDocumentData } from "../../store/document";
import { useLocation, useNavigate } from "react-router-dom";
import { getAllClients, getClientsList, loadClientList } from "../../store/clients";
import { getAllAppDocuments, getAppWfTasks, getClientAllApps, loadAllAppDocuments, loadAppWfTasks, loadClientAllApps } from "../../store/wfApp";
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Info as InfoIcon } from '@mui/icons-material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { getAssignedUserlist, loadAssignedUsers } from "../../store/users";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    whiteSpace: "nowrap",
  },
}));

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    padding: theme.spacing(2),
    fontSize: '0.875rem',
    fontfamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontWight: '400',
  },
}));

const headCells = [
  { id: "id", label: "Ticket ID" },
  { id: "dateTime", label: "Date & Time" },
  {
    id: "title",
    label: "Issue",
    editable: true,
  },
  {
    id: "customer",
    label: "Added By",
    sortable: false,
  },
  {
    id: "customer",
    label: "Assigned To",
    sortable: false,
  },
  {
    id: "attachment",
    label: "Attachment",
    sortable: false,
  },
  {
    id: "description",
    label: "Description",
    sortable: false,
  },
  {
    id: "discussion",
    label: "Discussion",
    sortable: false,
  },
  {
    id: "",
    label: "Workflow Phase",
    sortable: false,
  },
];

export default function ManageIssues() {

  const [openReviewPopup, setOpenReviewPopup] = useState(false);
  const [openDiscussionPopup, setOpenDiscussionPopup] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [pageSize, setPageSize] = useState(5);
  const [pageIndex, setPageIndex] = useState(1);
  const [allIssues, setAllIssues] = useState([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedApplicationId, setSelectedApplicationId] = useState("");
  const [clientList, setClientList] = useState([]);
  const [wfAllAppTasks, setWfAllAppTasks] = useState([]);
  const [allAppDocuments, setAllAppDocuments] = useState([]);
  const [selectedClientApps, setSelectedClientApps] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [allAssignedUsers, setAllAssignedUsers] = useState([]);


  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "", });
  const [filterFn, setFilterFn] = useState({ fn: (items) => { return items; } });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const issuesSelector = useSelector(getAllIssues);
  const allClientsSelector = useSelector(getClientsList);
  const appWfTasksSelector = useSelector(getAppWfTasks);
  const allAppDocumentsSelector = useSelector(getAllAppDocuments);
  const clientAllAppsSelector = useSelector(getClientAllApps);
  const downloadedDocumentDataSelector = useSelector(getDownloadedDocumentData);
  const assignedUserlistSelector = useSelector(getAssignedUserlist);

  const location = useLocation();

  useEffect(() => {
    if (location?.state?.clientId) {
      const { clientId, appId, pSize, pIndex } = location?.state;
      setSelectedClientId(clientId);
      setSelectedApplicationId(appId);
      setPageSize(pSize);
      setPageIndex(pIndex);
      setTimeout(() => {
        dispatch(loadIssues(pSize, pIndex, clientId));
      }, 3000);
    } else {
      dispatch(loadClientList());
    }
  }, [location]);

  useEffect(() => {
    if (issuesSelector?.isSuccess) {
      setAllIssues(issuesSelector.data);
      setRowsCount(issuesSelector.totalRecords);
      let ids = [];

      issuesSelector.data.forEach(item => {
        if (!ids.includes(item.ciid)) ids.push(item.ciid);
      });

      if (ids.length > 0 && selectedClientId) {
        const data = {
          ids: ids,
          clientId: selectedClientId
        }
        dispatch(loadAppWfTasks(data));
        dispatch(loadAllAppDocuments(data));
      }
    }
  }, [issuesSelector])

  useEffect(() => {
    if (allClientsSelector.length > 0) {
      const list = allClientsSelector.map((item) =>
        ({ id: item.id, title: item.name }));
      setClientList(list);
    }
  }, [allClientsSelector])

  useEffect(() => {
    if (appWfTasksSelector.length === 0) return;
    const newWfTasks = appWfTasksSelector.filter(item => {
      const isDuplicate = appWfTasksSelector.filter(x => x.wiid === item.wiid).length > 1;
      return !isDuplicate || item.taskDoneStatus === 4;
    });
    let usrIds = [];
    appWfTasksSelector.forEach(item => {
      if (item?.performerId > 0 && !usrIds.includes(item.performerId)) {
        usrIds.push(item.performerId);
      }
    });

    if (usrIds.length > 0) {
      dispatch(loadAssignedUsers(usrIds.map(id => `ids=${id}`).join("&")));
    }
    setWfAllAppTasks(newWfTasks);
  }, [appWfTasksSelector]);

  useEffect(() => {
   setAllAssignedUsers(assignedUserlistSelector);
  }, [assignedUserlistSelector]);

  useEffect(() => {
    setAllAppDocuments(allAppDocumentsSelector);
  }, [allAppDocumentsSelector]);

  useEffect(() => {
    if (clientAllAppsSelector.length > 0) {
      const list = clientAllAppsSelector.map((item) =>
        ({ id: item.workflowId, title: item.workflowTitle }));
      setSelectedClientApps(list);
    }
  }, [clientAllAppsSelector])

  const handlePageChange = (newPage) => {
    setPageIndex(newPage + 1);
    dispatch(loadIssues(pageSize, newPage + 1, selectedClientId));
  }

  const handleRowsPerPage = (rows) => {
    setPageSize(rows);
    setPageIndex(1);
    dispatch(loadIssues(rows, 1, selectedClientId));
  }

  const {
    TblContainer,
    TblHeader,
    TblPagination,
    tblDataAfterPaginationAndSorting,
  } = useTable(allIssues, headCells, filterFn, rowsCount, pageSize, pageIndex, handlePageChange, handleRowsPerPage);

  const handleSearch = (filterData) => {
    const frmData = new FormData();
    Object.keys(filterData).map(item =>
      frmData.append(item, filterData[item])
    )
    dispatch(loadIssuesByfilter(frmData))
  };

  const handlReviewIssue = (item, tsk) => {
    if (item === "save") {
      dispatch(loadIssues(pageSize, pageIndex, selectedClientId));
      setSelectedIssue(null);
      setOpenReviewPopup(false);
    }
    else {
      setSelectedIssue(item);
      navigate("/review", {
        state: {
          issue: item, isReview: true, wfTask: tsk, clientId: selectedClientId,
          appId: selectedApplicationId,
          pSize: pageSize,
          pIndex: pageIndex
        }
      });
    }
  };

  const handlReviewDiscuss = () => {
    setOpenDiscussionPopup(false)
  }

  const handleDiscussion = (item) => {
    setSelectedIssue(item);
    setOpenDiscussionPopup(true);
  };

  const handleDownloadFile = async (item) => {
    dispatch(downloadDocument(item.docId, selectedClientId));
    setSelectedDoc(item);
  };

  useEffect(() => {
    if (downloadedDocumentDataSelector) {
      try {
        const url = window.URL.createObjectURL(downloadedDocumentDataSelector);
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        let fileName = selectedDoc?.docName || "document";
        if (downloadedDocumentDataSelector?.type) {
          const mimeParts = downloadedDocumentDataSelector.type.split("/");
          const extension = mimeParts[1] || "bin";
          if (!fileName.includes(".")) {
            fileName += `.${extension}`;
          }
        } else if (!fileName.includes(".")) {
          fileName += ".bin";
        }

        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        dispatch(documentDownloaded(null));
      } catch (error) {
        console.error("Error downloading file:", error);
      }
    }
  }, [downloadedDocumentDataSelector]);


  const handleFilterReset = () => {
    dispatch(loadIssues(pageSize, pageIndex, selectedClientId));
  }

  const handleChangeClient = ({ target }) => {
    setSelectedClientId(target.value);
    dispatch(loadIssues(pageSize, pageIndex, target.value));
    dispatch(loadClientAllApps(target.value));
  }

  const handleChangeApplication = ({ target }) => {
    setSelectedApplicationId(target.value);
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <Controls.Select
            id="client"
            name="client"
            label="Select Customer"
            option={clientList}
            value={selectedClientId}
            onChange={handleChangeClient}
            sx={{ backgroundColor: "white" }}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          <Controls.Select
            id="application"
            name="application"
            label="Select Application"
            option={selectedClientApps}
            value={selectedApplicationId}
            onChange={handleChangeApplication}
            disabled={!selectedClientId}
            sx={{ backgroundColor: "white" }}
          />
        </Grid>
      </Grid>
      {selectedClientId && selectedApplicationId && (
        <>
          <Collapse sx={{ marginTop: "15px" }} in={openFilter}>
            <SearchPanel setOpen={setOpenFilter} handleSearch={handleSearch} handleFilterReset={handleFilterReset} />
          </Collapse>

          {!openFilter && (
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="flex-end"
              sx={{ mb: 2 }}
            >
              <Controls.Button
                color="primary"
                text="Filters"
                size="small"
                variant="outlined"
                onClick={() => setOpenFilter(true)}
                startIcon={<FilterAltIcon />}
                sx={{
                  width: "80px",
                  textTransform: 'none',
                  color: 'black',
                  border: "1px solid black",
                  '&:hover': {
                    backgroundColor: 'black',
                    color: 'white',

                  }
                }}
              />
            </Box>
          )}
          <Paper sx={{ overflow: "auto" }}>
            <TblContainer>
              <TblHeader />
              <TableBody>
                {tblDataAfterPaginationAndSorting().map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.ciid}</TableCell>
                    <StyledTableCell>
                      {format(new Date(item.addedDateTime), "MM/dd/yyyy")}
                      <br />
                      {format(new Date(item.addedDateTime), "h:mma")}
                    </StyledTableCell>
                    <StyledTableCell>{item.title}</StyledTableCell>
                    <TableCell>
                      {item.customerName}
                      <br />
                      {item.customerEmail}
                    </TableCell>
                    <TableCell>
                      {wfAllAppTasks?.filter(x => x.itemType === 1 && x.itemName !== "Acknowledgment" && x.ciid === item.ciid).map((task) => (
                        allAssignedUsers && allAssignedUsers?.filter(x => x.id === task?.performerId).map(user => (
                          <>
                            {user.firstName} {user.lastName}
                            <br />
                            {user.email}
                          </>
                        ))
                      ))}
                    </TableCell>
                    <StyledTableCell>
                      {allAppDocuments && allAppDocuments?.filter(x => x.ciid === item.ciid && x.documentType !== "Messaging").map((doc) => (
                        <>
                          <Link
                            underline="none"
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleDownloadFile(doc)}
                          >
                            {doc.docName}
                          </Link>
                          <br />
                        </>
                      ))}
                    </StyledTableCell>
                    <TableCell>
                      <LightTooltip title={item.description} arrow placement="top">
                        <IconButton>
                          <InfoIcon />
                        </IconButton>
                      </LightTooltip>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        component="span"
                        onClick={() => handleDiscussion(item)}
                      >
                        <MessageIcon />
                      </IconButton>
                    </TableCell>
                    <StyledTableCell>
                      <ButtonGroup variant="outlined"
                        orientation="vertical"
                        aria-label="button group">
                        {wfAllAppTasks?.filter(x => x.itemType === 1 && x.itemName !== "Acknowledgment" && x.ciid === item.ciid).map((task) => (
                          <Button
                            color={task.taskDoneStatus === 4 ? "success" : "primary"}
                            sx={{ justifyContent: "flex-start" }}
                            endIcon={task.taskDoneStatus == 4 ? <CheckIcon /> : <AutorenewIcon />}
                            onClick={() => task.taskDoneStatus === 4 ? "" : handlReviewIssue(item, task)}
                          >
                            {task.itemName}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TblContainer>
            <TblPagination />

            <Popup
              title="Review"
              openPopup={openReviewPopup}
              handleOpenPopup={handlReviewIssue}
              size="lg"
            >
              <ReviewForm
                setNotify={setNotify}
                setOpenPopup={setOpenReviewPopup}
                selectedIssue={selectedIssue}
                handlReviewIssue={handlReviewIssue}
              />
            </Popup>
            <Popup
              title="Discussion"
              openPopup={openDiscussionPopup}
              setOpenPopup={setOpenDiscussionPopup}
              handleOpenPopup={handlReviewDiscuss}
              size="xl"
            >
              <DiscussionPanel selectedIssue={selectedIssue} allAppDocuments={allAppDocuments} handleDownloadFile={handleDownloadFile} />
            </Popup>
            <Notification notify={notify} setNotify={setNotify} />
          </Paper>
        </>)}
    </>
  );
}
